import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from 'react-bootstrap';
import { Plus, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { supabase } from '../lib/supabaseClient';
import { fetchProfileLookups, formatProdi, hydrateProfile } from '../lib/profileLookups';
import {
  KriteriaType,
  normalizeFieldName,
  normalizeFieldType,
  normalizeKriteriaCode,
  parseFieldOptions,
} from '../lib/kriteriaMetadata';

const emptyKriteriaForm = {
  kode_kriteria: '',
  nama_kriteria: '',
  tipe_kriteria: KriteriaType.NARASI,
};

const emptyFieldForm = {
  nama_field: '',
  label: '',
  tipe_field: 'text',
  options: '',
  required: true,
};

const FIELD_TYPE_LABELS = {
  text: 'Text',
  number: 'Angka',
  select: 'Pilihan',
};

const getFieldTypeLabel = (type) => FIELD_TYPE_LABELS[type] || type || '-';

const formatFieldOptions = (options) =>
  parseFieldOptions(options)
    .map((option) => option.label || option.value)
    .join('\n');

function KriteriaManagementPage() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [lookups, setLookups] = useState({ prodiList: [] });
  const [criteriaRegistry, setCriteriaRegistry] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedProdiId, setSelectedProdiId] = useState('');
  const [selectedAssignmentId, setSelectedAssignmentId] = useState('');
  const [kriteriaForm, setKriteriaForm] = useState(emptyKriteriaForm);
  const [fieldForm, setFieldForm] = useState(emptyFieldForm);
  const [fieldEditId, setFieldEditId] = useState(null);
  const [fieldCodeTouched, setFieldCodeTouched] = useState(false);
  const [showKriteriaModal, setShowKriteriaModal] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const loadPage = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate('/');
      return;
    }

    const { data: profileData, error: profileError } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (profileError || !profileData) {
      setErrorMsg('Profile user tidak ditemukan.');
      setLoading(false);
      return;
    }

    if (profileData.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }

    try {
      const lookupsData = await fetchProfileLookups(supabase);
      const [criteriaResult, assignmentResult] = await Promise.all([
        supabase
          .schema('akreditasi')
          .from('kriteria')
          .select('id, kode_kriteria, nama_kriteria, urutan, is_active')
          .order('urutan', { ascending: true }),
        supabase
          .schema('akreditasi')
          .from('kriteria_prodi')
          .select(`
            id,
            kriteria_id,
            prodi_id,
            nama_kriteria_override,
            tipe_kriteria_override,
            is_active,
            kriteria:kriteria_id (
              id,
              kode_kriteria,
              nama_kriteria,
              urutan,
              is_active
            ),
            fields:kriteria_prodi_fields (
              id,
              nama_field,
              label,
              tipe_field,
              options,
              urutan,
              required,
              is_active
            )
          `),
      ]);

      if (criteriaResult.error) {
        throw new Error(`Gagal mengambil kode kriteria: ${criteriaResult.error.message}`);
      }

      if (assignmentResult.error) {
        throw new Error(`Gagal mengambil struktur prodi: ${assignmentResult.error.message}`);
      }

      setProfile(hydrateProfile(profileData, lookupsData));
      setLookups(lookupsData);
      setCriteriaRegistry(criteriaResult.data || []);
      setAssignments(assignmentResult.data || []);

      const firstProdiId = selectedProdiId || lookupsData.prodiList?.[0]?.id || '';
      setSelectedProdiId(firstProdiId);
    } catch (error) {
      setErrorMsg(error.message);
    }

    setLoading(false);
  }, [navigate, selectedProdiId]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const selectedProdi = useMemo(
    () => lookups.prodiList.find((item) => item.id === selectedProdiId) || null,
    [lookups.prodiList, selectedProdiId]
  );

  const prodiAssignments = useMemo(
    () =>
      assignments
        .filter((item) => item.prodi_id === selectedProdiId)
        .slice()
        .sort((a, b) => (a.kriteria?.urutan || 0) - (b.kriteria?.urutan || 0)),
    [assignments, selectedProdiId]
  );

  const selectedAssignment = useMemo(
    () =>
      prodiAssignments.find((item) => item.id === selectedAssignmentId) ||
      prodiAssignments[0] ||
      null,
    [prodiAssignments, selectedAssignmentId]
  );

  const selectedFields = useMemo(
    () =>
      (selectedAssignment?.fields || [])
        .slice()
        .sort((a, b) => (a.urutan || 0) - (b.urutan || 0)),
    [selectedAssignment]
  );

  useEffect(() => {
    if (selectedAssignment && selectedAssignment.id !== selectedAssignmentId) {
      setSelectedAssignmentId(selectedAssignment.id);
    }
  }, [selectedAssignment, selectedAssignmentId]);

  const activeFields = selectedFields.filter((field) => field.is_active);

  const getAssignmentTitle = (assignment) =>
    assignment?.nama_kriteria_override ||
    assignment?.kriteria?.nama_kriteria ||
    '-';

  const getAssignmentType = (assignment) =>
    assignment?.tipe_kriteria_override ||
    KriteriaType.NARASI;

  const openAddKriteria = () => {
    const usedNumbers = prodiAssignments
      .map((item) => Number(String(item.kriteria?.kode_kriteria || '').replace(/\D/g, '')))
      .filter(Boolean);
    const nextNumber = usedNumbers.length > 0 ? Math.max(...usedNumbers) + 1 : 1;

    setKriteriaForm({
      kode_kriteria: `C${nextNumber}`,
      nama_kriteria: '',
      tipe_kriteria: KriteriaType.NARASI,
    });
    setShowKriteriaModal(true);
  };

  const saveKriteriaForProdi = async (e) => {
    e.preventDefault();
    if (!selectedProdi) return;

    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    const kodeKriteria = normalizeKriteriaCode(kriteriaForm.kode_kriteria);
    const urutan = Number(kodeKriteria.match(/\d+/)?.[0] || 0);

    if (!kodeKriteria || !kriteriaForm.nama_kriteria.trim() || !urutan) {
      setErrorMsg('Kode C, nama kriteria, dan nomor urutan wajib diisi.');
      setSaving(false);
      return;
    }

    let criteriaId = criteriaRegistry.find(
      (item) => normalizeKriteriaCode(item.kode_kriteria) === kodeKriteria
    )?.id;

    if (!criteriaId) {
      const { data, error } = await supabase
        .schema('akreditasi')
        .from('kriteria')
        .insert([
          {
            kode_kriteria: kodeKriteria,
            nama_kriteria: kodeKriteria,
            urutan,
            is_active: true,
          },
        ])
        .select('id')
        .single();

      if (error) {
        setErrorMsg(error.message);
        setSaving(false);
        return;
      }

      criteriaId = data.id;
    } else {
      const { error } = await supabase
        .schema('akreditasi')
        .from('kriteria')
        .update({ is_active: true, urutan })
        .eq('id', criteriaId);

      if (error) {
        setErrorMsg(error.message);
        setSaving(false);
        return;
      }
    }

    const { error } = await supabase
      .schema('akreditasi')
      .from('kriteria_prodi')
      .upsert(
        [
          {
            kriteria_id: criteriaId,
            prodi_id: selectedProdi.id,
            nama_kriteria_override: kriteriaForm.nama_kriteria.trim(),
            tipe_kriteria_override: kriteriaForm.tipe_kriteria,
            is_active: true,
          },
        ],
        { onConflict: 'kriteria_id,prodi_id' }
      );

    if (error) {
      setErrorMsg(error.message);
      setSaving(false);
      return;
    }

    setSuccessMsg(`${kodeKriteria} berhasil ditambahkan untuk ${formatProdi(selectedProdi)}.`);
    setShowKriteriaModal(false);
    setSaving(false);
    await loadPage();
  };

  const saveSelectedStructure = async (e) => {
    e.preventDefault();
    if (!selectedAssignment) return;

    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    const { error } = await supabase
      .schema('akreditasi')
      .from('kriteria_prodi')
      .update({
        nama_kriteria_override: kriteriaForm.nama_kriteria.trim(),
        tipe_kriteria_override: kriteriaForm.tipe_kriteria,
        is_active: true,
      })
      .eq('id', selectedAssignment.id);

    if (error) {
      setErrorMsg(error.message);
      setSaving(false);
      return;
    }

    setSuccessMsg('Struktur kriteria prodi berhasil diperbarui.');
    setSaving(false);
    await loadPage();
  };

  const openEditStructure = (assignment) => {
    setSelectedAssignmentId(assignment.id);
    setKriteriaForm({
      kode_kriteria: assignment.kriteria?.kode_kriteria || '',
      nama_kriteria: getAssignmentTitle(assignment),
      tipe_kriteria: getAssignmentType(assignment),
    });
  };

  useEffect(() => {
    if (selectedAssignment) {
      setKriteriaForm({
        kode_kriteria: selectedAssignment.kriteria?.kode_kriteria || '',
        nama_kriteria: getAssignmentTitle(selectedAssignment),
        tipe_kriteria: getAssignmentType(selectedAssignment),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssignment?.id]);

  const toggleAssignment = async (assignment) => {
    const { error } = await supabase
      .schema('akreditasi')
      .from('kriteria_prodi')
      .update({ is_active: !assignment.is_active })
      .eq('id', assignment.id);

    if (error) {
      setErrorMsg(error.message);
      return;
    }

    setSuccessMsg(assignment.is_active ? 'Kriteria prodi dinonaktifkan.' : 'Kriteria prodi diaktifkan.');
    await loadPage();
  };

  const openAddField = () => {
    setFieldEditId(null);
    setFieldCodeTouched(false);
    setFieldForm(emptyFieldForm);
    setShowFieldModal(true);
  };

  const openEditField = (field) => {
    setFieldEditId(field.id);
    setFieldCodeTouched(true);
    setFieldForm({
      nama_field: field.nama_field,
      label: field.label,
      tipe_field: normalizeFieldType(field.tipe_field),
      options: formatFieldOptions(field.options),
      required: field.required,
    });
    setShowFieldModal(true);
  };

  const handleFieldLabelChange = (value) => {
    setFieldForm((current) => ({
      ...current,
      label: value,
      nama_field: fieldCodeTouched ? current.nama_field : normalizeFieldName(value),
    }));
  };

  const handleFieldCodeChange = (value) => {
    setFieldCodeTouched(true);
    setFieldForm((current) => ({
      ...current,
      nama_field: normalizeFieldName(value),
    }));
  };

  const refreshSuggestedFieldCode = () => {
    setFieldCodeTouched(false);
    setFieldForm((current) => ({
      ...current,
      nama_field: normalizeFieldName(current.label),
    }));
  };

  const saveField = async (e) => {
    e.preventDefault();
    if (!selectedAssignment) return;

    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    const fieldType = normalizeFieldType(fieldForm.tipe_field);
    const payload = {
      nama_field: normalizeFieldName(fieldForm.nama_field || fieldForm.label),
      label: fieldForm.label.trim(),
      tipe_field: fieldType,
      options: fieldType === 'select' ? parseFieldOptions(fieldForm.options) : [],
      required: fieldForm.required,
      is_active: true,
    };

    if (!payload.nama_field || !payload.label) {
      setErrorMsg('Kode isian dan judul isian wajib diisi.');
      setSaving(false);
      return;
    }

    const request = fieldEditId
      ? supabase
          .schema('akreditasi')
          .from('kriteria_prodi_fields')
          .update(payload)
          .eq('id', fieldEditId)
      : supabase
          .schema('akreditasi')
          .from('kriteria_prodi_fields')
          .insert([
            {
              ...payload,
              kriteria_prodi_id: selectedAssignment.id,
              urutan: selectedFields.length + 1,
            },
          ]);

    const { error } = await request;

    if (error) {
      setErrorMsg(error.message);
      setSaving(false);
      return;
    }

    setSuccessMsg(fieldEditId ? 'Isian berhasil diperbarui.' : 'Isian berhasil ditambahkan.');
    setShowFieldModal(false);
    setSaving(false);
    await loadPage();
  };

  const toggleField = async (field) => {
    const { error } = await supabase
      .schema('akreditasi')
      .from('kriteria_prodi_fields')
      .update({ is_active: !field.is_active })
      .eq('id', field.id);

    if (error) {
      setErrorMsg(error.message);
      return;
    }

    setSuccessMsg(field.is_active ? 'Isian dinonaktifkan.' : 'Isian diaktifkan.');
    await loadPage();
  };

  if (loading) {
    return (
      <DashboardLayout title="Kelola Kriteria" profile={profile} activeMenu="settings">
        <div className="page-loading-v2">
          <Spinner animation="border" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Kelola Kriteria"
      breadcrumbs={['Dashboard', 'Pengaturan', 'Kriteria']}
      profile={profile}
      activeMenu="settings"
    >
      <Container fluid className="p-0">
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
        {successMsg && <Alert variant="success">{successMsg}</Alert>}

        <Card className="content-card-v2 mb-4">
          <Card.Body>
            <div className="section-head-v2 mb-3">
              <div>
                <h4 className="section-title-v2">Struktur Kriteria per Prodi</h4>
              </div>
              <Button className="save-btn-v2" onClick={openAddKriteria} disabled={!selectedProdi}>
                <Plus size={16} className="me-2" />
                Tambah Kriteria
              </Button>
            </div>

            <Row className="g-4">
              <Col xl={3}>
                <Form.Label>Program Studi</Form.Label>
                <Form.Select
                  value={selectedProdiId}
                  onChange={(e) => {
                    setSelectedProdiId(e.target.value);
                    setSelectedAssignmentId('');
                  }}
                >
                  {lookups.prodiList.map((prodi) => (
                    <option value={prodi.id} key={prodi.id}>
                      {formatProdi(prodi)}
                    </option>
                  ))}
                </Form.Select>

                <div className="archive-side-meta mt-3">
                  {prodiAssignments.map((assignment) => (
                    <button
                      type="button"
                      className={`report-attention-item text-start ${
                        selectedAssignment?.id === assignment.id ? 'active' : ''
                      }`}
                      key={assignment.id}
                      onClick={() => openEditStructure(assignment)}
                    >
                      <div>
                        <strong>{assignment.kriteria?.kode_kriteria}</strong>
                        <span>{getAssignmentTitle(assignment)}</span>
                      </div>
                      <Badge bg={assignment.is_active ? 'success' : 'secondary'}>
                        {assignment.is_active ? 'aktif' : 'nonaktif'}
                      </Badge>
                    </button>
                  ))}

                  {prodiAssignments.length === 0 && (
                    <div className="criterion-softdelete-note">
                      Belum ada kriteria untuk prodi ini.
                    </div>
                  )}
                </div>
              </Col>

              <Col xl={9}>
                {selectedAssignment ? (
                  <>
                    <Card className="content-card-v2 mb-4">
                      <Card.Body>
                        <div className="section-head-v2 mb-3">
                          <div>
                            <h4 className="section-title-v2">
                              {selectedAssignment.kriteria?.kode_kriteria} - {formatProdi(selectedProdi)}
                            </h4>
                          </div>
                          <Button
                            variant={selectedAssignment.is_active ? 'outline-danger' : 'outline-success'}
                            onClick={() => toggleAssignment(selectedAssignment)}
                          >
                            {selectedAssignment.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                          </Button>
                        </div>

                        <Form onSubmit={saveSelectedStructure}>
                          <Row className="g-3">
                            <Col md={3}>
                              <Form.Label>Kode</Form.Label>
                              <Form.Control value={kriteriaForm.kode_kriteria} disabled />
                            </Col>
                            <Col md={6}>
                              <Form.Label>Nama Kriteria untuk Prodi Ini</Form.Label>
                              <Form.Control
                                value={kriteriaForm.nama_kriteria}
                                onChange={(e) =>
                                  setKriteriaForm({ ...kriteriaForm, nama_kriteria: e.target.value })
                                }
                                required
                              />
                            </Col>
                            <Col md={3}>
                              <Form.Label>Format Data</Form.Label>
                              <Form.Select
                                value={kriteriaForm.tipe_kriteria}
                                onChange={(e) =>
                                  setKriteriaForm({ ...kriteriaForm, tipe_kriteria: e.target.value })
                                }
                              >
                                <option value={KriteriaType.NARASI}>Narasi</option>
                                <option value={KriteriaType.TABEL}>Tabel</option>
                              </Form.Select>
                            </Col>
                          </Row>

                          <div className="criterion-form-actions">
                            <Button className="save-btn-v2" type="submit" disabled={saving}>
                              Simpan Struktur
                            </Button>
                          </div>
                        </Form>
                      </Card.Body>
                    </Card>

                    <Card className="content-card-v2">
                      <Card.Body>
                        <div className="section-head-v2 mb-3">
                          <div>
                            <h4 className="section-title-v2">Daftar Isian Kriteria</h4>
                          </div>
                          <Button variant="outline-primary" onClick={openAddField}>
                            <Plus size={16} className="me-2" />
                            Tambah Isian
                          </Button>
                        </div>

                        <Table responsive hover className="table-v2 align-middle">
                          <thead>
                            <tr>
                              <th>Urutan</th>
                              <th>Kode Isian</th>
                              <th>Judul Isian</th>
                              <th>Jenis Isian</th>
                              <th>Status</th>
                              <th>Aksi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedFields.map((field) => (
                              <tr key={field.id}>
                                <td>{field.urutan}</td>
                                <td>{field.nama_field}</td>
                                <td>{field.label}</td>
                                <td>{getFieldTypeLabel(field.tipe_field)}</td>
                                <td>
                                  <Badge bg={field.is_active ? 'success' : 'secondary'}>
                                    {field.is_active ? 'Aktif' : 'Nonaktif'}
                                  </Badge>
                                </td>
                                <td>
                                  <div className="d-flex gap-2 flex-wrap">
                                    <Button size="sm" variant="outline-primary" onClick={() => openEditField(field)}>
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant={field.is_active ? 'outline-danger' : 'outline-success'}
                                      onClick={() => toggleField(field)}
                                    >
                                      {field.is_active ? 'Hapus' : 'Aktifkan'}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                            {selectedFields.length === 0 && (
                              <tr>
                                <td colSpan={6} className="text-center py-4">
                                  Belum ada isian. Tambahkan isian langsung untuk kriteria prodi ini.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>

                        <div className="criterion-softdelete-note">
                          Isian aktif: {activeFields.length}
                        </div>
                      </Card.Body>
                    </Card>
                  </>
                ) : (
                  <Card className="content-card-v2">
                    <Card.Body>
                      <div className="criterion-softdelete-note">
                        Pilih prodi lalu tambah kriteria pertama untuk mulai menyusun struktur.
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Modal show={showKriteriaModal} onHide={() => setShowKriteriaModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Tambah Kriteria untuk {formatProdi(selectedProdi)}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={saveKriteriaForProdi}>
            <Modal.Body>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Kode</Form.Label>
                  <Form.Control
                    value={kriteriaForm.kode_kriteria}
                    onChange={(e) =>
                      setKriteriaForm({ ...kriteriaForm, kode_kriteria: e.target.value })
                    }
                    placeholder="C1"
                    required
                  />
                </Col>
                <Col md={8}>
                  <Form.Label>Nama Kriteria untuk Prodi Ini</Form.Label>
                  <Form.Control
                    value={kriteriaForm.nama_kriteria}
                    onChange={(e) =>
                      setKriteriaForm({ ...kriteriaForm, nama_kriteria: e.target.value })
                    }
                    required
                  />
                </Col>
                <Col md={12}>
                <Form.Label>Format Data</Form.Label>
                  <Form.Select
                    value={kriteriaForm.tipe_kriteria}
                    onChange={(e) =>
                      setKriteriaForm({ ...kriteriaForm, tipe_kriteria: e.target.value })
                    }
                  >
                    <option value={KriteriaType.NARASI}>Narasi</option>
                    <option value={KriteriaType.TABEL}>Tabel</option>
                  </Form.Select>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowKriteriaModal(false)}>
                Batal
              </Button>
              <Button className="save-btn-v2" type="submit" disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showFieldModal} onHide={() => setShowFieldModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{fieldEditId ? 'Edit Isian' : 'Tambah Isian'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={saveField}>
            <Modal.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Judul Isian</Form.Label>
                  <Form.Control
                    value={fieldForm.label}
                    onChange={(e) => handleFieldLabelChange(e.target.value)}
                    placeholder="Latar Belakang"
                    required
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Kode Isian</Form.Label>
                  <InputGroup>
                    <Form.Control
                      value={fieldForm.nama_field}
                      onChange={(e) => handleFieldCodeChange(e.target.value)}
                      placeholder="latar_belakang"
                    />
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Gunakan kode dari judul isian</Tooltip>}
                    >
                      <Button
                        variant="outline-secondary"
                        type="button"
                        aria-label="Gunakan kode dari judul isian"
                        onClick={refreshSuggestedFieldCode}
                        disabled={!fieldForm.label.trim()}
                      >
                        <RefreshCw size={16} />
                      </Button>
                    </OverlayTrigger>
                  </InputGroup>
                </Col>
                <Col md={6}>
                  <Form.Label>Jenis Isian</Form.Label>
                  <Form.Select
                    value={fieldForm.tipe_field}
                    onChange={(e) => setFieldForm({ ...fieldForm, tipe_field: e.target.value })}
                  >
                    <option value="text">Text</option>
                    <option value="number">Angka</option>
                    <option value="select">Pilihan</option>
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <Form.Label>Wajib</Form.Label>
                  <Form.Select
                    value={String(fieldForm.required)}
                    onChange={(e) =>
                      setFieldForm({ ...fieldForm, required: e.target.value === 'true' })
                    }
                  >
                    <option value="true">Ya</option>
                    <option value="false">Tidak</option>
                  </Form.Select>
                </Col>
                {fieldForm.tipe_field === 'select' && (
                  <Col md={12}>
                    <Form.Label>Daftar Pilihan</Form.Label>
                    <Form.Control
                      as="textarea"
                      value={fieldForm.options}
                      onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value })}
                      placeholder={'Contoh:\nSangat Baik\nBaik\nCukup\nKurang'}
                    />
                  </Col>
                )}
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowFieldModal(false)}>
                Batal
              </Button>
              <Button className="save-btn-v2" type="submit" disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </DashboardLayout>
  );
}

export default KriteriaManagementPage;
